import { NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'

import { CapAutomatorConfig } from '../../data/cap-automator/types'
import { ChartResultWrapper, createChartConfig } from '../charts/components/ChartResultWrapper'
import { RateChart } from '../charts/components/rate-chart/RateChart'
import { EmptyStatusPanel } from './components/EmptyStatusPanel'
import { Header } from './components/Header'
import { InfoTilesGrid } from './components/InfoTilesGrid'
import { SparkRewardsBadge } from './components/SparkRewardsBadge'
import { SparkRewardsInfoTile } from './components/SparkRewardsInfoTile'
import { StatusPanelGrid } from './components/StatusPanelGrid'
import { Subheader } from './components/Subheader'
import { StatusIcon } from './components/status-icon/StatusIcon'
import { formatPercentage } from '@/domain/common/format'
import { SimplifiedQueryResult } from '@/domain/common/query'
import { Token } from '@/domain/types/Token'
import { MarketSparkRewards } from '@/features/spark-rewards/types'
import { BorrowEligibilityStatus } from '@/features/sparklend/common/data/market-info/reserve-status'
import { InfoTile } from '@/features/sparklend/market-details/components/info-tile/InfoTile'
import { MarketRateChartData } from '@/features/sparklend/market-details/data/market-charts/types'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { CooldownTimer } from '@/ui/molecules/cooldown-timer/CooldownTimer'
import { TokenAmountTooltip } from '@/ui/organisms/token-amount-tooltip/TokenAmountTooltip'
import { UsdAmountTooltip } from '@/ui/organisms/usd-amount-tooltip/UsdAmountTooltip'
import { testIds } from '@/ui/utils/testIds'

interface BorrowStatusPanelProps {
  status: BorrowEligibilityStatus
  token: Token
  totalBorrowed: NormalizedNumber
  borrowLiquidity: NormalizedNumber
  limitedByBorrowCap: boolean
  borrowCap?: NormalizedNumber
  reserveFactor: Percentage
  apy: Percentage | undefined
  showTokenBadge?: boolean
  capAutomatorInfo?: CapAutomatorConfig
  sparkRewards: MarketSparkRewards[]
  borrowRateHistoricResult?: SimplifiedQueryResult<MarketRateChartData>
}

export function BorrowStatusPanel({
  status,
  token,
  totalBorrowed,
  borrowLiquidity,
  limitedByBorrowCap,
  borrowCap,
  reserveFactor,
  apy,
  capAutomatorInfo,
  sparkRewards,
  borrowRateHistoricResult,
}: BorrowStatusPanelProps) {
  if (status === 'no') {
    return <EmptyStatusPanel status={status} variant="borrow" />
  }

  const borrowRateChartConfig =
    borrowRateHistoricResult &&
    createChartConfig({
      component: RateChart,
      props: { data: borrowRateHistoricResult.data ?? [], metricDisplayName: 'Borrow Rate (APR)' },
      isError: borrowRateHistoricResult.isError,
      isPending: borrowRateHistoricResult.isPending,
    })

  return (
    <StatusPanelGrid>
      <StatusIcon status={status} />
      <Header status={status} variant="borrow" />
      <SparkRewardsBadge sparkRewards={sparkRewards} />
      <Subheader status={status} />
      <InfoTilesGrid>
        <InfoTile>
          <InfoTile.Label>Total borrowed</InfoTile.Label>
          <InfoTile.Value>
            <TokenAmountTooltip
              token={token}
              amount={totalBorrowed}
              options={{ style: 'compact', showSymbol: 'always' }}
            />
          </InfoTile.Value>
          <InfoTile.ComplementaryLine>
            <UsdAmountTooltip token={token} amount={totalBorrowed} style="compact" />
          </InfoTile.ComplementaryLine>
        </InfoTile>
        <InfoTile>
          <InfoTile.Label>
            <ApyTooltip variant="borrow">Borrow APY</ApyTooltip>
          </InfoTile.Label>
          <InfoTile.Value>{formatPercentage(apy)}</InfoTile.Value>
        </InfoTile>
        <SparkRewardsInfoTile sparkRewards={sparkRewards} />

        <InfoTile>
          <InfoTile.Label>Reserve factor</InfoTile.Label>
          <InfoTile.Value>{formatPercentage(reserveFactor)}</InfoTile.Value>
        </InfoTile>

        {borrowCap && <BorrowCapInfoTile token={token} borrowCap={borrowCap} capAutomatorInfo={capAutomatorInfo} />}
        <BorrowLiquidityInfoTile
          token={token}
          borrowLiquidity={borrowLiquidity}
          limitedByBorrowCap={limitedByBorrowCap}
          capAutomatorInfo={capAutomatorInfo}
        />
      </InfoTilesGrid>

      {borrowRateChartConfig && (
        <div className="col-span-3 mt-6 sm:col-span-2 sm:col-start-2 sm:mt-10">
          <ChartResultWrapper {...borrowRateChartConfig} height={200} />
        </div>
      )}
    </StatusPanelGrid>
  )
}

interface BorrowCapInfoTileProps {
  token: Token
  borrowCap: NormalizedNumber
  capAutomatorInfo?: CapAutomatorConfig
}

function BorrowCapInfoTile({ token, borrowCap, capAutomatorInfo }: BorrowCapInfoTileProps) {
  const maxCap = capAutomatorInfo?.maxCap ?? borrowCap

  return (
    <InfoTile>
      <InfoTile.Label>Borrow cap</InfoTile.Label>
      <InfoTile.Value data-testid={testIds.marketDetails.borrowStatusPanel.cap}>
        <TokenAmountTooltip token={token} amount={maxCap} options={{ style: 'compact', showSymbol: 'always' }} />
      </InfoTile.Value>
      <InfoTile.ComplementaryLine>
        <UsdAmountTooltip token={token} amount={maxCap} style="compact" />
      </InfoTile.ComplementaryLine>
    </InfoTile>
  )
}

interface BorrowLiquidityInfoTileProps {
  token: Token
  borrowLiquidity: NormalizedNumber
  limitedByBorrowCap: boolean
  capAutomatorInfo?: CapAutomatorConfig
}

function BorrowLiquidityInfoTile({
  token,
  capAutomatorInfo,
  borrowLiquidity,
  limitedByBorrowCap,
}: BorrowLiquidityInfoTileProps) {
  return (
    <InfoTile>
      <InfoTile.Label>Instantly available liquidity</InfoTile.Label>
      <InfoTile.Value data-testid={testIds.marketDetails.borrowStatusPanel.liquidity}>
        <TokenAmountTooltip
          token={token}
          amount={borrowLiquidity}
          options={{ style: 'compact', showSymbol: 'always' }}
        />
        {limitedByBorrowCap && capAutomatorInfo && (
          <CooldownTimer
            renewalPeriod={capAutomatorInfo.increaseCooldown}
            latestUpdateTimestamp={capAutomatorInfo.lastIncreaseTimestamp}
            token={token}
            gap={capAutomatorInfo.gap}
            cooldownOverContent={
              <>The available liquidity is constrained by instant borrow cap, which may be adjusted at any time. </>
            }
            cooldownActiveContent={
              <>
                The liquidity is constrained by instant borrow cap, which has a renewal time of{' '}
                {secondsToHours(capAutomatorInfo.increaseCooldown)} hours.{' '}
              </>
            }
          />
        )}
      </InfoTile.Value>
      <InfoTile.ComplementaryLine>
        <UsdAmountTooltip token={token} amount={borrowLiquidity} style="compact" />
      </InfoTile.ComplementaryLine>
    </InfoTile>
  )
}

function secondsToHours(seconds: number) {
  return Math.floor(seconds / 3600)
}
